import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Play, Pause, Loader2, Headphones, RotateCcw, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPEEDS = [1, 1.5, 2];
const fmt = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(t / 3600); const m = Math.floor((t % 3600) / 60); const sec = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

// Audiobook player: sequential MP3 parts behind one slider; plays only the editor's chosen stretch unless `full` (editor preview)
export const AudioPlayerModal = ({ book, token, color = '#0d9488', onClose, full = false, extra }) => {
  const audio = useRef(null);
  const pending = useRef(null);
  const [parts, setParts] = useState(null);
  const [error, setError] = useState('');
  const [idx, setIdx] = useState(0);
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [drag, setDrag] = useState(null);
  const [win, setWin] = useState({ start: 0, end: 0 });

  useEffect(() => {
    axios.get(`${API}/digital/books/${book.id}/audiobook/parts`, { params: { auth: token } })
      .then(({ data }) => {
        const dur = data.parts.reduce((t, p) => t + p.duration, 0);
        setWin(full ? { start: 0, end: dur } : { start: data.trim_start || 0, end: data.trim_end || dur });
        setParts(data.parts);
      })
      .catch((e) => setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Não foi possível carregar o áudio.'));
  }, [book.id, token]);

  const offsets = (parts || []).reduce((acc, p, i) => [...acc, (acc[i] || 0) + p.duration], [0]);
  const abs = (offsets[idx] || 0) + cur;
  const total = Math.max(0, win.end - win.start);
  const position = Math.max(0, abs - win.start);
  const src = (i) => `${API}/digital/books/${book.id}/audiobook/part/${i}?auth=${encodeURIComponent(token)}`;

  const seekAbs = (t) => {
    if (!parts) return;
    const target = Math.min(Math.max(win.start, t), Math.max(win.start, win.end - 0.5));
    const i = Math.max(0, offsets.findIndex((o, k) => k < parts.length && target < offsets[k + 1]));
    const local = target - offsets[i];
    if (i !== idx) { pending.current = { t: local, play: playing }; setIdx(i); setCur(local); } else if (audio.current) { audio.current.currentTime = local; setCur(local); }
  };
  const seekTo = (rel) => seekAbs(win.start + rel);

  useEffect(() => { if (parts && win.start > 0) seekAbs(win.start); }, [parts]);

  const onTime = (e) => {
    const t = e.target.currentTime;
    setCur(t);
    if ((offsets[idx] || 0) + t >= win.end - 0.05 && !e.target.paused) e.target.pause();
  };

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) { if (abs >= win.end - 0.5) seekAbs(win.start); a.play().catch(() => {}); } else a.pause();
  };

  const onLoaded = () => {
    const a = audio.current;
    a.playbackRate = speed;
    if (pending.current) { a.currentTime = pending.current.t; if (pending.current.play) a.play().catch(() => {}); pending.current = null; }
  };

  const onEnded = () => {
    if (idx < parts.length - 1) { pending.current = { t: 0, play: true }; setIdx(idx + 1); setCur(0); } else setPlaying(false);
  };

  const changeSpeed = (s) => { setSpeed(s); if (audio.current) audio.current.playbackRate = s; };
  const shown = drag ?? position;

  return (
    <div onClick={onClose} data-testid="audio-player-modal" style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(5,8,12,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, maxHeight: '94vh', overflowY: 'auto', borderRadius: 18, color: 'white', background: `linear-gradient(160deg, ${color} 0%, #0B0F14 120%)`, boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem 0' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.8 }}><Headphones className="w-4 h-4" />Audiolivro</span>
          <button onClick={onClose} data-testid="audio-player-close" aria-label="Fechar" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 999, width: 34, height: 34, color: 'white', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X className="w-4 h-4" /></button>
        </div>
        <div style={{ padding: '1rem 1.5rem 1.5rem', textAlign: 'center' }}>
          {book.capa_url && <img src={book.capa_url.startsWith('http') ? book.capa_url : `${process.env.REACT_APP_BACKEND_URL}${book.capa_url}`} alt="" style={{ width: 120, aspectRatio: '190 / 300', objectFit: 'cover', borderRadius: 8, margin: '0 auto 1rem', boxShadow: '0 12px 30px rgba(0,0,0,0.35)' }} />}
          <p data-testid="audio-player-title" style={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3, marginBottom: '1.25rem' }}>{book.titulo}</p>
          {error ? <p data-testid="audio-player-error" style={{ color: '#FFB4B4' }}>{error}</p> : !parts ? <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '1rem auto' }} /> : (
            <>
              <audio ref={audio} src={src(idx)} preload="metadata" onLoadedMetadata={onLoaded} onTimeUpdate={onTime}
                onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={onEnded} />
              <input type="range" min={0} max={Math.max(1, total)} step={1} value={shown} data-testid="audio-player-seek" aria-label="Posição do áudio"
                onChange={(e) => setDrag(Number(e.target.value))} onMouseUp={() => { seekTo(drag ?? position); setDrag(null); }}
                onTouchEnd={() => { seekTo(drag ?? position); setDrag(null); }} onKeyUp={() => { if (drag != null) { seekTo(drag); setDrag(null); } }}
                style={{ width: '100%', accentColor: 'white', cursor: 'pointer' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', opacity: 0.8, marginTop: 2 }}>
                <span data-testid="audio-player-current">{fmt(shown)}</span><span data-testid="audio-player-total">{fmt(total)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', margin: '1rem 0' }}>
                <button onClick={() => seekTo(position - 15)} data-testid="audio-player-back" aria-label="Voltar 15 segundos" style={ghost}><RotateCcw className="w-5 h-5" /></button>
                <button onClick={toggle} data-testid="audio-player-toggle" aria-label={playing ? 'Pausar' : 'Tocar'}
                  style={{ width: 64, height: 64, borderRadius: 999, border: 'none', background: 'white', color: '#0B0F14', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                  {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7" style={{ marginLeft: 3 }} />}
                </button>
                <button onClick={() => seekTo(position + 15)} data-testid="audio-player-forward" aria-label="Avançar 15 segundos" style={ghost}><RotateCw className="w-5 h-5" /></button>
              </div>
              <div style={{ display: 'inline-flex', gap: 6, background: 'rgba(255,255,255,0.1)', padding: 4, borderRadius: 999 }}>
                {SPEEDS.map((s) => (
                  <button key={s} onClick={() => changeSpeed(s)} data-testid={`audio-speed-${String(s).replace('.', '_')}`}
                    style={{ border: 'none', borderRadius: 999, padding: '0.35rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', background: speed === s ? 'white' : 'transparent', color: speed === s ? '#0B0F14' : 'white' }}>
                    {String(s).replace('.', ',')}x
                  </button>
                ))}
              </div>
              {extra && extra({ abs, seekAbs, duration: offsets[offsets.length - 1] || 0 })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ghost = { background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 999, width: 44, height: 44, color: 'white', cursor: 'pointer', display: 'grid', placeItems: 'center' };
